import { useEffect, useState } from "react";

// Mirrors core.WebRankings (GET /v1/web/rankings). Rank, totals, and the
// winnings display string all come from the server.
export type RankingRow = {
  rank: number;
  username: string;
  avatar_url?: string;
  contests_entered: number;
  contests_won: number;
  total_winnings_cents: number;
  total_winnings_display: string;
};

export type RankingStats = { producers_ranked: number; contests_entered: number; contests_won: number };

export type RankingsState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; rows: RankingRow[]; stats: RankingStats };

const RANKINGS_URL = "/v1/web/rankings";

const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);

// Keep only the fields the page shows. A body without rows and stats is
// malformed, and the page says so rather than showing zeros.
export function normalizeRankings(raw: unknown): { rows: RankingRow[]; stats: RankingStats } | null {
  if (!raw || typeof raw !== "object") return null;
  const b = raw as { rows?: unknown; stats?: unknown };
  if (!Array.isArray(b.rows) || !b.stats || typeof b.stats !== "object") return null;
  const s = b.stats as Record<string, unknown>;
  const rows = b.rows
    .filter((r): r is Record<string, unknown> => !!r && typeof r === "object" && typeof r.username === "string")
    .map((r) => ({
      rank: num(r.rank),
      username: r.username as string,
      avatar_url: typeof r.avatar_url === "string" && /^https?:\/\//.test(r.avatar_url) ? r.avatar_url : undefined,
      contests_entered: num(r.contests_entered),
      contests_won: num(r.contests_won),
      total_winnings_cents: num(r.total_winnings_cents),
      total_winnings_display: typeof r.total_winnings_display === "string" ? r.total_winnings_display : "",
    }));
  return {
    rows,
    stats: {
      producers_ranked: num(s.producers_ranked),
      contests_entered: num(s.contests_entered),
      contests_won: num(s.contests_won),
    },
  };
}

export function useRankings(): RankingsState {
  const [state, setState] = useState<RankingsState>({ status: "loading" });
  useEffect(() => {
    const ctrl = new AbortController();
    fetch(RANKINGS_URL, { method: "GET", signal: ctrl.signal, headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`rankings ${res.status}`))))
      .then((raw) => {
        const data = normalizeRankings(raw);
        setState(data ? { status: "ready", ...data } : { status: "error" });
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setState({ status: "error" });
      });
    return () => ctrl.abort();
  }, []);
  return state;
}

export type RankingSort = "won" | "entered" | "winnings";

// Reorders by the chosen number, highest first; the server's rank breaks ties.
export function sortRows(rows: RankingRow[], sort: RankingSort): RankingRow[] {
  const value = (r: RankingRow) =>
    sort === "entered" ? r.contests_entered : sort === "winnings" ? r.total_winnings_cents : r.contests_won;
  return [...rows].sort((a, b) => value(b) - value(a) || a.rank - b.rank);
}
